<template>
  <div class="content-light">
    <h1>Help and FAQs</h1>

    <ul class="faq">
      <li>
        <a v-on:click="showFaq(1)">When will I receive my first issue?</a>
      </li>
      <li>
        <a v-on:click="showFaq(2)">How do I make a change to my order?</a>
      </li>
      <li>
        <a v-on:click="showFaq(3)"
          >My subscription includes a free gift. Who will it be sent to and when?</a
        >
      </li>
      <li v-if="currentProduct.fulfilment.digital_access_link">
        <a v-on:click="showFaq(4)">How do I access my free digital copy?</a>
      </li>
      <li v-if="currentProduct.fulfilment.payment_method == 'DD'">
        <a v-on:click="showFaq(5)">When will my direct debit be taken for my subscription?</a>
      </li>
      <li>
        <a v-on:click="showFaq(6)">How do I create an e-card for my order?</a>
      </li>
      <li>
        <a v-on:click="showFaq(7)">I would like to contact someone about my subscription</a>
      </li>
    </ul>
    <a v-on:click="findOrder" class="button-second">Track other order</a>

    <div class="overlay-wrapper" v-if="currentQuestion !== null">
      <div class="dialog">
        <button v-on:click="currentQuestion = null">&#x2715;</button>
        <div class="questions">
          <div v-if="currentQuestion == 1">
            <h3>When will I receive my first issue?</h3>
            <p>
              We expect the first issue of a weekly title to arrive within 2 to 4 weeks of order and
              a monthly title within 4 to 8 weeks. A bi-monthly or quarterly will likely take a
              little longer depending on how recently the previous issue was released.
            </p>
            <p v-if="isNationalGeographic">
              (Nat Geo) National Geographic is delivered from the US and it prints and labels its
              editions a significant amount of time ahead of the date they are delivered.
              Accordingly, there can be a long lead time between being added to the subscriber file
              and the first issue of your subscription arriving (the first issue has been known to
              take between 8 and 14 weeks to arrive). Once the first issue arrives, subsequent
              issues will arrive at the expected frequency and without delay.
            </p>
            <p v-if="currentProduct.is_xmas_gift">
              This is a Christmas Gift Order. We have communicated this to the publisher and your
              subscription should start with the first issue released after Christmas Day.
            </p>
            <p>
              If your first issue hasn't arrived after the expected timescale, or you would like to
              find out the specific start date of your subscription please view 'I would like to
              contact someone about my subscription' in the Help section.
            </p>
            <p v-if="currentProduct.fulfilment.digital_access_link">
              Your subscription also includes instant access to a digital copy of the latest issue
              from when your order was placed.
              <a :href="currentProduct.fulfilment.digital_access_link" target="_blank"
                >Click here</a
              >
              to read.
            </p>
          </div>
          <div v-else-if="currentQuestion == 2">
            <h3>How do I make a change to my order?</h3>
            <p>
              Please go to the Help section
              <a v-on:click="showFaq(7)">I would like to contact someone about my subscription</a>
              and share details of your order and any changes you would like to make with the
              contact shown.
            </p>
          </div>
          <div v-else-if="currentQuestion == 3">
            <h3>
              My subscription includes a free gift. Who will it be sent to and when?
            </h3>
            <p>
              The free gift will be sent to the billing name and address, unless specified in the
              subscription offer itself. It will be sent separately from the magazine and can take
              up to 12 weeks to arrive – but it will be worth the wait!
            </p>
          </div>
          <div v-else-if="currentQuestion == 4">
            <h3>How do I access my free digital copy?</h3>
            <p>
              To access your free digital copy please
              <a :href="currentProduct.fulfilment.digital_access_link" target="_blank">Click here</a
              >.
            </p>
            <p>
              If you would like to view the digital copy on a second device you will be required to
              create an account with our sister site Pocketmags.com. You can then use this account
              to read via the link, or through the Pocketmags.com website or Pocketmags app on iOS
              or Google Play.
            </p>
          </div>
          <div v-else-if="currentQuestion == 5">
            <h3>When will my direct debit be taken for my subscription?</h3>
            <p>
              Direct Debit payments are set up by the publisher and it may take up to four weeks for
              the bank to authorise the payment.
            </p>
          </div>
          <div v-else-if="currentQuestion == 6">
            <h3>How do I create an e-card for my order?</h3>
            <p>
              With our
              <a href="https://www.magazine.co.uk/ecard" target="_blank">FREE Gift Card Creator</a>
              you can choose from a selection of fab designs. Then you can either print or email a
              personalised message for the recipient of your gift subscription.
            </p>
          </div>
          <div v-else-if="currentQuestion == 7">
            <h3>I would like to contact someone about my subscription</h3>

            <p v-if="notFulfilledSoon">
              Please contact us at
              <a href="mailto:enquiries@magazine.co.uk">enquiries@magazine.co.uk</a>
              stating your order number - <b>#{{ order.order_id }}</b> and details of how we can
              help you.
            </p>
            <div v-else>
              <p>
                Your order is now with the publisher and they can be contacted using the details
                shown below.
              </p>
              <p>
                Along with your question(s), we recommend copying and pasting the order details
                below into your message to the publisher so that they have everything required to
                identify your order and help promptly:
              </p>
              <div>
                <span>Publisher contact details: </span>
                <br />
                <br />
                <span>
                  <span v-if="copyEmailText" style="float: right">
                    {{ copyEmailText }}
                  </span>
                  <a :href="'mailto:' + currentProduct.publisher.email" style="color: blue">{{
                    currentProduct.publisher.email
                  }}</a>

                  &nbsp;

                  <div
                    class="button-copy"
                    v-on:click="copyToClipboardBasic(currentProduct.publisher.email)"
                    title="Copy to clipboard"
                  >
                    <img
                      class="copy-icon"
                      src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABMAAAAYCAYAAAAYl8YPAAAA/UlEQVRIS+3VsSuFURjH8c9dDFYGKVLKYFEWi5JB/Bf+A8lkQLeUifwL/gmDYlSWm01RIillYDJYXD111KF7z+slk/vUWc77PN/393vOeZ+3oRwL2MMkLrCG424ljQIrAOfoy3JeMYXLTnUBm8A+RpHDBzCEE6xjF3N4wFMGa+MOq1HcwnRB4Ra209oo5LUC9pYUreAZTYxnRS84whL6s/2r9ILB5KwdsJAZMYJ7nGGm4mDi8SlmMYab2Phz2OYXm91Ehs2dKmXfcPgppWizB+vYgV7P6l6Mis+pLu4/H8AiHus2DMM4/JhnMdiiib+N6xiO8zhIP5SfAm+x/A4R3FYR9GvPPAAAAABJRU5ErkJggg=="
                    />
                    &nbsp;
                    <span>Copy Email</span>
                  </div>
                </span>
              </div>
              <br />
              <div>
                <span v-if="copyTableText" style="float: right">
                  {{ copyTableText }}
                </span>

                <span>Order details:</span>
                &nbsp;

                <div
                  class="button-copy"
                  v-on:click="copyToClipboard('FulfilmentTable')"
                  title="Copy table to clipboard"
                >
                  <img
                    class="copy-icon"
                    src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABMAAAAYCAYAAAAYl8YPAAAA/UlEQVRIS+3VsSuFURjH8c9dDFYGKVLKYFEWi5JB/Bf+A8lkQLeUifwL/gmDYlSWm01RIillYDJYXD111KF7z+slk/vUWc77PN/393vOeZ+3oRwL2MMkLrCG424ljQIrAOfoy3JeMYXLTnUBm8A+RpHDBzCEE6xjF3N4wFMGa+MOq1HcwnRB4Ra209oo5LUC9pYUreAZTYxnRS84whL6s/2r9ILB5KwdsJAZMYJ7nGGm4mDi8SlmMYab2Phz2OYXm91Ehs2dKmXfcPgppWizB+vYgV7P6l6Mis+pLu4/H8AiHus2DMM4/JhnMdiiib+N6xiO8zhIP5SfAm+x/A4R3FYR9GvPPAAAAABJRU5ErkJggg=="
                  />
                  &nbsp;
                  <span>Copy Order Details</span>
                </div>

                <br />
                <br />

                <table id="FulfilmentTable">
                  <tr>
                    <th>Name</th>
                    <td>
                      {{ currentProduct.fulfilment.billing.title }}
                      {{ currentProduct.fulfilment.billing.first_name }}
                      {{ currentProduct.fulfilment.billing.last_name }}
                    </td>
                  </tr>
                  <tr>
                    <th>Email</th>
                    <td>{{ currentProduct.fulfilment.email }}</td>
                  </tr>
                  <tr>
                    <th>Product</th>
                    <td>{{ currentProduct.magazine_name }}</td>
                  </tr>
                  <tr v-if="currentProduct.offer_type">
                    <th class="nw">Sub type</th>
                    <td>{{ currentProduct.offer_type }}</td>
                  </tr>
                  <tr>
                    <th class="nw">Order Placed</th>
                    <td>{{ order.transaction_datetime }}</td>
                  </tr>
                  <tr>
                    <th class="nw">Jellyfish Fulfilment Id</th>
                    <td>{{ currentProduct.fulfilment.fulfilment_id }}</td>
                  </tr>
                  <tr>
                    <th class="nw">Sent to bureau</th>
                    <td>
                      <span v-if="currentProduct.sent_to_publisher_at">{{currentProduct.sent_to_publisher_at}}</span>
                      <span v-else-if="currentProduct.fulfilment.next_fulfilment">Will be sent {{currentProduct.fulfilment.next_fulfilment}}</span>
                      <span v-else>Will be sent to publisher soon</span>
                    </td>
                  </tr>
                </table>

                <br />
              </div>
            </div>
            <br />

            <!--For any orders where publisher collection date is >24 hours away (ie so it won’t soon be switching to ‘sent to publisher’:-->
          </div>
          <a class="close" v-on:click="currentQuestion = null">Close</a>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  name: "Faq",
  props: {
    currentProduct: {
      type: Object,
      default: null,
      required: true
    },
    order: {
      type: Object,
      default: null,
      required: true
    },
    openQuestion: {
      type: Number,
      default: null,
      required: false
    }
  },
  data() {
    return {
      currentQuestion: this.openQuestion,
      copyEmailText: "",
      copyTableText: ""
    };
  },
  watch: {
    openQuestion: function(val) {
      this.currentQuestion = val;
    }
  },

  methods: {
    showFaq(questionNumber) {
      this.currentQuestion = questionNumber;
    },
    copyToClipboardBasic(text) {
      navigator.clipboard.writeText(text);

      this.copyEmailText = "Copied";
      window.setTimeout(() => {
        this.copyEmailText = "";
      }, 2000);
    },
    copyToClipboard(containerid) {
      var target = document.getElementById(containerid);

      var success = false;
      if (target.type === "textarea") {
        target.select();
        target.setSelectionRange(0, 99999); /*For mobile devices*/
        success = document.execCommand("copy");
      } else {
        this.selectElement(target);
        success = document.execCommand("copy");
      }

      this.deselectElements();

      this.copyTableText = "Copied";
      window.setTimeout(() => {
        this.copyTableText = "";
      }, 2000);
    },
    selectElement(element) {
      if (window.getSelection) {
        var range = document.createRange();
        range.selectNodeContents(element);
        window.getSelection().removeAllRanges();
        window.getSelection().addRange(range);
      } else if (document.selection) {
        var range = document.body.createTextRange();
        range.moveToElementText(element);
      } else {
        console.error("COULDN'T SELECT :/");
      }
    },
    deselectElements() {
      window.getSelection().removeAllRanges();
    },
    findOrder() {
      this.$router.push("/");
    }
  },
  computed: {
    isNationalGeographic() {
      return this.currentProduct.campaign_ref == "NGEZ";
    },
    notFulfilledSoon() {
      //look at next fulfilled date (or if fulfilled already)
      //if not then
      return this.currentProduct.status === 1 && this.moreThanDayAway;
    },
    moreThanDayAway() {
      if(this.currentProduct.fulfilment.next_fulfilment === null)
        return true;

      var yourDate = new Date(this.currentProduct.fulfilment.next_fulfilment);
      var OneDay = new Date().getTime() + 1 * 24 * 60 * 60 * 1000; //day hour  min  sec  msec

      return OneDay < yourDate;
    }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
#FulfilmentTable {
  th,
  td {
    padding: 3px 20px 3px 0;
  }
}

.button-second {
  margin: 0px 0;
  align-self: flex-start;
  min-width: 100px;
  text-align: center;
}

.button-copy {
  vertical-align: middle;
  cursor: pointer;
  border: 1px solid #888;
  border-radius: 5px;
  display: inline-block;
  padding: 4px 5px;
  font-size: 12px;
  background: #eee;
  box-shadow: #aaa 0px 2px 3px 0px;

  .copy-icon {
    vertical-align: middle;
    height: 16px;
  }
}
</style>
